/**
 * Perception (Movement spec §5) — the gate between "sees" and "acts". Bots are not
 * omniscient: everything reaches the AI only through this layer.
 *
 * - Vision cone: full range in a forward cone, plus a shorter 360° awareness ring
 *   (peripheral / hearing).
 * - Line of sight: supercover against blockers; shroom clouds block sight past 2 tiles.
 * - Reaction latency: a sighting can't be acted on until the agent's tier reaction
 *   time has elapsed since it was first seen (an event queue with timestamps, not a
 *   setTimeout).
 * - Memory: a lost target's last known position lingers for 4 s so the agent paths
 *   there and searches before giving up — never instant forget / instant re-acquire.
 */

export interface VisionConfig {
  range: number;          // tiles, full-cone reach
  coneDeg: number;        // forward cone width (spec: 120)
  awarenessFrac: number;  // 360° awareness as a fraction of range (spec: 0.4)
}

export const DEFAULT_VISION: Record<string, VisionConfig> = {
  skeleton: { range: 10, coneDeg: 120, awarenessFrac: 0.4 },
  orc: { range: 12, coneDeg: 120, awarenessFrac: 0.4 },
  arena_bot: { range: 18, coneDeg: 120, awarenessFrac: 0.4 },
};

/** Is (tx,ty) inside the viewer's cone or awareness ring? (Geometry only, no LOS.) */
export function inVisionCone(vx: number, vy: number, faceAngle: number, tx: number, ty: number, cfg: VisionConfig): boolean {
  const dx = tx - vx, dy = ty - vy;
  const d = Math.hypot(dx, dy);
  if (d > cfg.range) return false;
  if (d <= cfg.range * cfg.awarenessFrac) return true;   // 360° awareness up close
  const to = Math.atan2(dy, dx);
  let diff = Math.abs(((to - faceAngle) % (2 * Math.PI)));
  if (diff > Math.PI) diff = 2 * Math.PI - diff;
  return diff <= (cfg.coneDeg * Math.PI) / 180 / 2;
}

/**
 * Supercover line of sight. `blocks(tx,ty)` = a wall/mushroom/rock; `shroomAt(tx,ty)`
 * (optional) marks shroom tiles that cumulatively cut sight after `shroomMax` tiles.
 */
export function lineOfSight(
  x0: number, y0: number, x1: number, y1: number,
  blocks: (tx: number, ty: number) => boolean,
  shroomAt?: (tx: number, ty: number) => boolean,
  shroomMax = 2,
): boolean {
  let tx = Math.floor(x0), ty = Math.floor(y0);
  const tx1 = Math.floor(x1), ty1 = Math.floor(y1);
  const dx = x1 - x0, dy = y1 - y0;
  const stepX = Math.sign(dx), stepY = Math.sign(dy);
  const tDeltaX = dx !== 0 ? Math.abs(1 / dx) : Infinity;
  const tDeltaY = dy !== 0 ? Math.abs(1 / dy) : Infinity;
  let tMaxX = dx !== 0 ? (stepX > 0 ? (tx + 1 - x0) : (x0 - tx)) * tDeltaX : Infinity;
  let tMaxY = dy !== 0 ? (stepY > 0 ? (ty + 1 - y0) : (y0 - ty)) * tDeltaY : Infinity;
  let shroom = 0, guard = 0;
  while ((tx !== tx1 || ty !== ty1) && guard++ < 512) {
    if (tMaxX < tMaxY) { tMaxX += tDeltaX; tx += stepX; }
    else { tMaxY += tDeltaY; ty += stepY; }
    if (tx === tx1 && ty === ty1) break;                 // reached the target tile
    if (blocks(tx, ty)) return false;
    if (shroomAt && shroomAt(tx, ty)) { if (++shroom > shroomMax) return false; }
  }
  return true;
}

export interface Sighting { id: string; x: number; y: number; firstSeen: number; lastSeen: number; }

const MEMORY_MS = 4000;

/**
 * Per-agent perception memory with reaction latency. The AI calls observe() for each
 * enemy that passed the cone + LOS test this tick, then reads actable()/lastKnown().
 */
export class Perception {
  private mem = new Map<string, Sighting>();
  constructor(private reactionMs: number) {}

  /** Record a live sighting (caller already confirmed cone + LOS). */
  observe(now: number, id: string, x: number, y: number) {
    const s = this.mem.get(id);
    if (s) { s.x = x; s.y = y; s.lastSeen = now; }
    else this.mem.set(id, { id, x, y, firstSeen: now, lastSeen: now });
  }

  /** Drop memories whose last sighting is older than 4 s. */
  forget(now: number) {
    for (const [id, s] of this.mem) if (now - s.lastSeen > MEMORY_MS) this.mem.delete(id);
  }

  /** Sightings old enough to act on (seen at least reactionMs ago). */
  actable(now: number): Sighting[] {
    const out: Sighting[] = [];
    for (const s of this.mem.values()) if (now - s.firstSeen >= this.reactionMs) out.push(s);
    return out;
  }

  /** A remembered target's last position + how stale it is, or null if forgotten. */
  lastKnown(id: string, now: number): { x: number; y: number; ageMs: number } | null {
    const s = this.mem.get(id);
    if (!s || now - s.lastSeen > MEMORY_MS) return null;
    return { x: s.x, y: s.y, ageMs: now - s.lastSeen };
  }

  /** True while the agent still remembers (but may not currently see) a target. */
  remembers(id: string, now: number): boolean {
    const s = this.mem.get(id);
    return !!s && now - s.lastSeen <= MEMORY_MS;
  }
}

/** Per-tier reaction time in ms (spec §5). A range → pick with an rng in [0,1). */
export const REACTION_MS: Record<string, [number, number]> = {
  rookie: [400, 700], regular: [250, 400], veteran: [160, 250], elite: [120, 160],
  mob: [300, 500],
};
export function rollReaction(tier: string, r: number): number {
  const [lo, hi] = REACTION_MS[tier] ?? REACTION_MS.regular;
  return lo + (hi - lo) * r;
}
